import React from "react";
import CustomContainer from "../Components/CustomContainer";
import Loader from "../Components/Loader";
import ConstructionSiteWrapper from "../Components/ConstructionSiteWrapper";
import {generatePath, useParams} from "react-router-dom";
import ConstructionSiteAction from "../../Actions/ConstructionSiteAction";
import Project from "../Home/Components/Project";
import {Breadcrumb, Button, Spinner} from "react-bootstrap";
import CustomTranslation from "../Components/CustomTranslation";
import LMRAAction from "../../Actions/LMRAAction";
import G from "../../Actions/GeneralAction";
import T from "../../Actions/TranslationAction";
import LMRAItem from "./Components/LMRAItem";
import CustomImageInput from "../Components/CustomImageInput";
import CustomInput from "../Components/CustomInput";
import UserAction from "../../Actions/UserAction";
import CustomDate from "../Components/CustomDate";
import CustomCheckBox from "../Components/CustomCheckBox";
import CustomSelect from "../Components/CustomSelect";
import AssignmentAction from "../../Actions/AssignmentAction";
import TranslationButton from "../Components/TranslationButton";
import TranslationHint from "../Components/TranslationHint";

class LMRAItemScreen extends React.Component {

    //constructor
    constructor(props) {
        super(props);

        //set state
        this.state = {
            processing: false,
            ...T.getInitialTranslationState()
        };

        this.originalAssignment = null;
    }

    componentDidMount = () => {
        this.updateElements();
        this.checkTranslationHint();
    }

    //show the translation hint balloon if the user hasn't seen it before
    checkTranslationHint = () => {
        const seen = T.hasSeenTranslationHint();
        if (!seen) {
            this.setState({ showTranslationHint: true });
        }
    }

    //dismiss the translation hint balloon and remember that it was seen
    dismissTranslationHint = () => {
        this.setState({ showTranslationHint: false });
        T.markTranslationHintSeen();
    }

    updateElements = async () => {

        //get construction site
        let cs = await ConstructionSiteAction.getConstructionSite(this.props.params.construction_site_id);

        //get last minute risk analysis
        let lmra = await LMRAAction.getLMRA(this.props.params.lmra_id);

        //get last minute risk analysis item
        let lmraItem = await LMRAAction.getItem(this.props.params.item_id);

        //get current user (needed for translation preferences as well as targetUser default below)
        let currentUser = await UserAction.getCurrentUser();

        //resolve the read-only category + item title shown above the assignment form, in the current UI language
        let categoryTitle = '';
        let itemTitle = '';
        if (lmraItem.lastMinuteRisk) {
            categoryTitle = await T.translateArray(lmraItem.lastMinuteRisk.parent.title);
            itemTitle = await T.translateArray(lmraItem.lastMinuteRisk.title);
        } else {
            categoryTitle = await T.translateArray(lmraItem.lastMinuteRiskInsert.lastMinuteRisk.title);
            itemTitle = lmraItem.lastMinuteRiskInsert.title;
        }

        //get users for construction site
        let users = await UserAction.getUserListConstructionSite(cs.id);

        //define user options
        let userOptions = [];

        //loop
        users.forEach(element => {
            let label = null;
            if ((!element.firstName) || (!element.lastName)) {
                label = element.email;
            } else {
                label = element.firstName + ' ' + element.lastName;
            }
            userOptions.push({
                label: label,
                value: element['@id']
            });
        });

        //data for assignment
        let currentAssignment = JSON.parse(JSON.stringify(lmraItem.assignments[0]));

        if (currentAssignment.targetUser === null) {
            if (lmraItem.lastMinuteRisk) {
                currentAssignment.title = await T.translateArray(lmraItem.lastMinuteRisk.title);
                currentAssignment.recommendation = await T.translateArray(lmraItem.lastMinuteRisk.recommendations);
            } else {
                currentAssignment.title = lmraItem.lastMinuteRiskInsert.title;
                currentAssignment.recommendation = '';
            }

            let targetUser = userOptions[0].value;
            if (currentUser !== null) {
                userOptions.forEach(element => {
                    if (element.value.includes(currentUser.id)) {
                        targetUser = element.value;
                    }
                });
            }

            currentAssignment.targetUser = {
                id: targetUser
            };
            currentAssignment.dateRequested = new Date();
        } else {
            currentAssignment.dateRequested = new Date(currentAssignment.dateRequested);
            currentAssignment.targetUser = {
                id: currentAssignment.targetUser['@id']
            };
        }

        //check if photo is available
        if (currentAssignment.photo) {
            currentAssignment.imageId = currentAssignment.photo.id;
            currentAssignment.imageApiId = currentAssignment.photo['@id'];
        }

        if (currentAssignment.altPhoto) {
            currentAssignment.image2Id = currentAssignment.altPhoto.id;
            currentAssignment.image2ApiId = currentAssignment.altPhoto['@id'];
        }

        //data for user
        let user = userOptions[0];
        if (currentAssignment.targetUser !== null) {
            //try to find in options
            userOptions.forEach((value) => {
                if (value.value === currentAssignment.targetUser.id) {
                    user = value;
                }
            });
        }

        //keep an immutable snapshot of the original (untranslated) assignment fields, used as the source for translation
        if (!this.originalAssignment) {
            this.originalAssignment = { ...currentAssignment };
        }

        //pick up the active translation for this LMRA, if any (e.g. set from the list screen -
        //navigation here is a hard page reload, so this is the only thing that survives it)
        const session = T.getTranslationSession(this.props.params.lmra_id);

        //load available DeepL languages lazily, same as Assignment translation
        const languageState = await T.loadAvailableLanguages(
            currentUser, this.state.availableLanguages, session?.selectedLanguage ?? this.state.selectedLanguage
        ) ?? {};

        const showTranslation = session ? session.showTranslation : this.state.showTranslation;
        const activeLanguage = session?.selectedLanguage ?? languageState.selectedLanguage ?? this.state.selectedLanguage;

        //recompute the translated overlay if a translation is currently active
        let translationState = {};
        if (showTranslation && activeLanguage) {
            const translated = await this.translateLMRAItem(activeLanguage.value, categoryTitle, itemTitle);
            translationState = {
                displayAssignment: translated.displayAssignment,
                displayCategoryTitle: translated.categoryTitle,
                displayItemTitle: translated.itemTitle,
            };
        }

        //set state
        this.setState({
            data: {
                constructionSite: cs,
                lastMinuteRiskAnalysis: lmra,
                lastMinuteRiskAnalysisItem: lmraItem
            },
            userOptions: userOptions,
            user: user,
            currentAssignment: currentAssignment,
            currentUser: currentUser,
            categoryTitle: categoryTitle,
            itemTitle: itemTitle,
            ...languageState,
            ...translationState,
            showTranslation,
            selectedLanguage: activeLanguage,
        });
    }

    //translate the prefilled assignment fields + read-only category/item title in one batched DeepL call
    translateLMRAItem = async (targetLang, categoryTitle, itemTitle) => {
        const source = this.originalAssignment || this.state.currentAssignment;

        const { translated, extraTranslations } = await T.translateFlatFields(
            source,
            ['title', 'riskDescription', 'immediateActionTaken', 'recommendation'],
            targetLang,
            { extraTexts: [categoryTitle, itemTitle] }
        );

        return {
            displayAssignment: translated,
            categoryTitle: extraTranslations[0],
            itemTitle: extraTranslations[1],
        };
    }

    handleTranslate = async (language, rememberChoice = false) => {
        this.setState({ selectedLanguage: language, showTranslation: true, loadingTranslations: true });
        T.setTranslationSession(this.props.params.lmra_id, { selectedLanguage: language, showTranslation: true });

        const translated = await this.translateLMRAItem(language.value, this.state.categoryTitle, this.state.itemTitle);
        this.setState({
            displayAssignment: translated.displayAssignment,
            displayCategoryTitle: translated.categoryTitle,
            displayItemTitle: translated.itemTitle,
            loadingTranslations: false,
        });

        if (rememberChoice && this.state.currentUser?.id) {
            const patch = await T.rememberLanguage(this.state.currentUser.id, language);
            this.setState(prev => ({
                currentUser: { ...prev.currentUser, ...patch }
            }));
        }
    }

    handleShowOriginal = () => {
        this.setState({ showTranslation: false, displayAssignment: null, displayCategoryTitle: null, displayItemTitle: null });
        T.setTranslationSession(this.props.params.lmra_id, { selectedLanguage: this.state.selectedLanguage, showTranslation: false });
    }

    //data from image upload
    handleUpload = async (file, name) => {
        let newState = {};
        newState[`${name}Id`] = file.id;
        newState[`${name}ApiId`] = file['@id'];
        this.setStateAssignment(newState);
    }

    //set state for current assignment. Also merges into `displayAssignment` (if a translation is
    //currently shown) since the form fields are controlled inputs - without this, an edit made while
    //translated would be overwritten by the frozen translated snapshot on the very next render.
    setStateAssignment = (object) => {
        this.setState(prev => ({
            currentAssignment: { ...prev.currentAssignment, ...object },
            displayAssignment: prev.displayAssignment ? { ...prev.displayAssignment, ...object } : prev.displayAssignment,
        }));
    }

    render = () => {
        if (this.state.data) {

            //use translated fields if available, otherwise the original prefilled ones
            const assignment = this.state.displayAssignment || this.state.currentAssignment;
            const categoryTitleText = this.state.displayCategoryTitle || this.state.categoryTitle;
            const itemTitleText = this.state.displayItemTitle || this.state.itemTitle;

            //resolve the user's saved DeepL language preference, if any, so the translate button can skip asking
            const savedLanguage = T.resolveSavedLanguage(this.state.currentUser, this.state.availableLanguages);

            return (
                <CustomContainer
                    footer={
                        <Button
                            block
                            disabled={
                                this.state.processing ||
                                ( !(this.state.currentAssignment.concept) &&
                                  !(this.state.currentAssignment.riskDescription && this.state.currentAssignment.immediateActionTaken &&
                                    this.state.currentAssignment.targetUser && this.state.currentAssignment.targetUser.id &&
                                    this.state.currentAssignment.title && this.state.currentAssignment.dateRequested &&
                                    this.state.currentAssignment.recommendation)
                                )
                            }
                            variant={'dark'}
                            onClick={() => {
                                this.setState({processing: true}, () => {
                                    AssignmentAction.edit(this.state.currentAssignment).then((result) => {
                                        if (result) {
                                            setTimeout(() => {
                                                window.location.href = generatePath('/construction_site/:construction_site_id/last_minute_risk_analysis/:lmra_id', {construction_site_id: this.state.data.constructionSite.id, lmra_id: this.state.data.lastMinuteRiskAnalysis.id});
                                            }, 1000);
                                        }
                                     }).then(() => {
                                        setTimeout(() => {
                                            this.setState({processing: false});
                                        }, 1000);
                                     });
                                });  
                            }}
                        >
                            <CustomTranslation value={'KEY.Add LMRA'} />
                            {this.state.processing &&
                                <Spinner size={'sm'} animation={'border'} variant={'white'} style={{margin: 2}} />
                            }
                        </Button>
                    }
                >
                    <ConstructionSiteWrapper
                        constructionSite={this.state.data.constructionSite}
                    >
                        <div>
                            {/* Project header */}
                            <div style={{position: 'sticky', top: '2rem', zIndex: 9}}>
                                <Project
                                    float
                                    data={this.state.data.constructionSite}
                                />
                            </div>

                            {/* Breadcrumb */}
                            <Breadcrumb className={'vertical-margin-2'}>
                                <Breadcrumb.Item
                                    href={generatePath('/')}
                                >
                                    <CustomTranslation value={'KEY.Home'} />
                                </Breadcrumb.Item>
                                <Breadcrumb.Item
                                    href={generatePath('/construction_site/:id', {id: this.state.data.constructionSite.id})}
                                >
                                    {this.state.data.constructionSite.name}
                                </Breadcrumb.Item>
                                <Breadcrumb.Item
                                    href={generatePath('/construction_site/:construction_site_id/last_minute_risk_analysis/:lmra_id', {construction_site_id: this.state.data.constructionSite.id, lmra_id: this.state.data.lastMinuteRiskAnalysis.id})}
                                >
                                    <CustomTranslation value={'KEY.Last Minute Risk Analysis'} />
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>
                                    <CustomTranslation value={'KEY.Item'} />
                                </Breadcrumb.Item>
                            </Breadcrumb>

                            {/* Title */}
                            <div className={'vertical-margin'} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                                <h1>
                                    <CustomTranslation value={'KEY.Last Minute Risk Analysis'}/>
                                </h1>
                                <TranslationButton
                                    showTranslation={this.state.showTranslation}
                                    loading={this.state.loadingTranslations}
                                    availableLanguages={this.state.availableLanguages}
                                    savedLanguage={savedLanguage}
                                    onTranslate={this.handleTranslate}
                                    onShowOriginal={this.handleShowOriginal}
                                    onFirstInteract={this.dismissTranslationHint}
                                />
                                <TranslationHint
                                    visible={this.state.showTranslationHint}
                                    onDismiss={this.dismissTranslationHint}
                                />
                            </div>

                            {/* User */}
                            <div className={'vertical-margin-2'}>
                                <div
                                    className={'bordered rounded vertical-padding-2 standard-padding-2'}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <p className={'label unbold'}>
                                            <CustomTranslation value={'KEY.By'} />
                                        </p>
                                        <p className={'label unbold'}>
                                            {G.formatDate(this.state.data.lastMinuteRiskAnalysis.createdAt)}
                                        </p>
                                    </div>
                                    <h3>
                                        {
                                            (this.state.data.lastMinuteRiskAnalysis.createdBy.firstName || this.state.data.lastMinuteRiskAnalysis.createdBy.lastName)
                                                ?
                                                (this.state.data.lastMinuteRiskAnalysis.createdBy.firstName || '') + ' ' + (this.state.data.lastMinuteRiskAnalysis.createdBy.lastName || '')
                                                :
                                                this.state.data.lastMinuteRiskAnalysis.createdBy.email
                                        }
                                    </h3>
                                </div>
                            </div>

                            {/* Lmra item */}
                            <div className={'vertical-margin-2'}>
                                <div
                                    className={'bordered rounded vertical-padding-2 standard-padding-2'}
                                >
                                    {/* Header */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <p className={'label unbold'} style={{paddingBottom: 0, flexGrow: 1, flexShrink: 1, wordBreak: 'break-word'}}>
                                            {categoryTitleText}
                                        </p>
                                        <div className={'standard-padding-2'}>
                                            <i className={'las la-thumbs-up text-success'} style={{fontSize: '1.4rem'}} />
                                        </div>
                                        <div>
                                            <i className={'las la-thumbs-down text-primary'} style={{fontSize: '1.4rem'}} />
                                        </div>
                                    </div>
                                    {/* Item */}
                                    <LMRAItem
                                        className={'vertical-margin-3'}
                                        title={itemTitleText}
                                        value={this.state.data.lastMinuteRiskAnalysisItem.status}
                                        onValueChange={(value) => {
                                            let element = this.state.data.lastMinuteRiskAnalysisItem;
                                            element.status = value;
                                            LMRAAction.updateItem(element).then((result) => {
                                                //if true
                                               if (value === true || value === null) {
                                                   window.location.href = generatePath('/construction_site/:construction_site_id/last_minute_risk_analysis/:lmra_id', {construction_site_id: this.state.data.constructionSite.id, lmra_id: this.state.data.lastMinuteRiskAnalysis.id});
                                               }
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Assignment form */}
                            <div className={'vertical-margin-2'}>
                                <h4><CustomTranslation value={'KEY.LMRA assignment'} /></h4>
                                <div
                                    className={'bordered rounded vertical-padding-2 standard-padding-2'}
                                >
                                    <div>
                                        <CustomImageInput
                                            label={<CustomTranslation value={'KEY.Image overview'} />}
                                            onUpload={(val) => this.handleUpload(val, 'image')}
                                            imageId={this.state.currentAssignment.imageId ? this.state.currentAssignment.imageId : null}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomImageInput
                                            label={<CustomTranslation value={'KEY.Image detail'} />}
                                            onUpload={(val) => this.handleUpload(val, 'image2')}
                                            imageId={this.state.currentAssignment.image2Id ? this.state.currentAssignment.image2Id : null}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomInput
                                            label={<CustomTranslation value={'KEY.Title'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            value={assignment.title}
                                            onChangeText={(value) => {
                                                this.setStateAssignment({title: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomInput
                                            label={<CustomTranslation value={'KEY.Description'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            value={assignment.riskDescription}
                                            onChangeText={(value) => {
                                                this.setStateAssignment({riskDescription: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomInput
                                            label={<CustomTranslation value={'KEY.Actions taken (by me)'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            value={assignment.immediateActionTaken}
                                            inputProps={{
                                                as: 'textarea'
                                            }}
                                            onChangeText={(value) => {
                                                this.setStateAssignment({immediateActionTaken: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomInput
                                            label={<CustomTranslation value={'KEY.Recommendation'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            value={assignment.recommendation}
                                            inputProps={{
                                                as: 'textarea'
                                            }}
                                            onChangeText={(value) => {
                                                this.setStateAssignment({recommendation: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomDate
                                            label={<CustomTranslation value={'KEY.Solve by'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            value={this.state.currentAssignment.dateRequested}
                                            onChange={(value) => {
                                                this.setStateAssignment({dateRequested: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomCheckBox
                                            label={<CustomTranslation value={'KEY.Finished'} />}
                                            value={this.state.currentAssignment.finished}
                                            onValueChange={(value) => {
                                                this.setStateAssignment({finished: value});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomSelect
                                            label={<CustomTranslation value={'KEY.Assign to'} />}
                                            labelAppend={!this.state.currentAssignment.concept ? '*' : ''}
                                            options={this.state.userOptions}
                                            selected={this.state.user}
                                            onSelect={(value, label) => {
                                                this.setStateAssignment({targetUser: {id: value}});
                                                this.setState({user: {value: value, label: label}});
                                            }}
                                        />
                                    </div>
                                    <div className={'vertical-margin-3'}>
                                        <CustomCheckBox
                                            label={<CustomTranslation value={'KEY.Save as concept'} />}
                                            value={this.state.currentAssignment.concept}
                                            onValueChange={(value) => {
                                                this.setStateAssignment({concept: value});
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>
                    </ConstructionSiteWrapper>
                </CustomContainer>
            );
        } else {
            return (
                <CustomContainer>
                    <Loader />
                </CustomContainer>
            );
        }
    }
}

//export
export default function (props) {
    const params = useParams();
    return <LMRAItemScreen {...props} params={params} />;
}